import { createTheme, alpha } from "@mui/material/styles";

const ACCENT = "#38bdf8";
const ACCENT_STRONG = "#0ea5e9";
const BG_DEFAULT = "#0b1320";
const BG_PAPER = "#111c2e";
const BG_CARD = "#1a2945";
const LINE = "rgba(255,255,255,0.08)";

export const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: ACCENT, dark: ACCENT_STRONG, contrastText: "#0b1320" },
    secondary: { main: "#fb7185" },
    success: { main: "#84cc16" },
    warning: { main: "#fbbf24" },
    background: { default: BG_DEFAULT, paper: BG_PAPER },
    text: { primary: "#e7eef9", secondary: "#9aa6b6" },
    divider: LINE,
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily:
      '"Space Grotesk", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
    overline: { letterSpacing: "0.08em", fontWeight: 700 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10 },
        outlined: {
          borderColor: LINE,
          "&:hover": { borderColor: alpha(ACCENT, 0.6), bgcolor: alpha(ACCENT, 0.04) },
        },
      },
    },

    // 16px on inputs prevents iOS Safari auto-zoom on focus.
    MuiInputBase: { styleOverrides: { input: { fontSize: 16 } } },
    MuiSelect: { styleOverrides: { select: { fontSize: 16 } } },

    // Outlined fields: soft accent border on hover, glowing focus ring.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: alpha("#000", 0.15),
          "& .MuiOutlinedInput-notchedOutline": { borderColor: LINE },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(ACCENT, 0.5),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: ACCENT,
            borderWidth: 1,
          },
          "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.18)}` },
        },
      },
    },

    // Checkbox: filled-blue square (instead of bare outline) — matches
    // the old "pill" look with a clear active state.
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 6,
          color: alpha("#fff", 0.35),
          "&.Mui-checked": { color: ACCENT },
          "& .MuiSvgIcon-root": { fontSize: 22 },
        },
      },
    },

// Cards: subtle gradient hint of warmth on the dark surface.
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: BG_CARD,
          backgroundImage: "none",
          border: `1px solid ${LINE}`,
        },
      },
    },

    // Menus / Drawers: dark paper with no extra elevation tint.
    MuiMenu: {
      styleOverrides: {
        paper: { backgroundColor: BG_PAPER, backgroundImage: "none" },
      },
    },

    // Menu items: MUI ships body1 (weight 400) which feels too thin on
    // the dark paper. Bump both the item itself and any inner
    // ListItemText primary so language/currency labels read crisp.
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          "& .MuiListItemText-primary": { fontWeight: 600 },
        },
      },
    },

    // MUI Chip
    //  - keep the legacy color coding via .chip--* class
    //  - rounder pill, slightly bolder font
    //  - outlined variant: subtle tint mixed from currentColor so each
    //    chip echoes its species/method colour instead of looking flat.
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          height: 26,
        },
        sizeSmall: { height: 24 },
        label: {
          overflow: "visible",
          textOverflow: "clip",
          paddingLeft: 10,
          paddingRight: 10,
          // All chip labels in caps + Space Grotesk for a uniform
          // techy badge feel. Letter-spacing tames the uppercase a bit.
          fontFamily:
            '"Space Grotesk", system-ui, sans-serif',
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        },
        outlined: {
          borderColor: "currentColor",
          backgroundColor: "transparent",
        },
        icon: {
          color: "currentColor",
          width: 14,
          height: 14,
          marginLeft: 8,
          marginRight: -4,
        },
        iconSmall: { width: 13, height: 13, marginLeft: 7, marginRight: -4 },
      },
    },

    // Toggle-buttons (basemap, etc.) inherit the accent ring on hover.
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          "&.Mui-selected": {
            backgroundColor: alpha(ACCENT, 0.18),
            color: ACCENT,
            "&:hover": { backgroundColor: alpha(ACCENT, 0.24) },
          },
        },
      },
    },

    // IconButtons get a subtle accent tint on hover.
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": { backgroundColor: alpha(ACCENT, 0.08) },
        },
      },
    },
  },
});
